import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import StudentPortalBanner from '../components/student/StudentPortalBanner';
import StudentRepository from '../repositories/StudentRepository';
import { buildPasswordHash } from '../services/PasswordService';
import { buildTokenHash } from '../utils/crypto';
import { useSettings } from '../store/SettingsContext';

export default function StudentPortalResetPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const repository = useMemo(() => new StudentRepository(), []);
  const { settings } = useSettings();
  const portalSettings = settings?.studentPortal || {};
  const [status, setStatus] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [studentId, setStudentId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const token = searchParams.get('token') || '';
    if (!token) {
      setStatus('No reset token provided.');
      return;
    }
    const checkToken = async () => {
      const tokenHash = await buildTokenHash(token);
      const students = repository.getAll();
      const match = students.find((entry) => entry.portalResetTokenHash === tokenHash);
      if (!match) {
        setStatus('Reset link is invalid or expired.');
        setTokenValid(false);
        return;
      }
      if (match.portalResetExpiresAt && new Date(match.portalResetExpiresAt) < new Date()) {
        setStatus('Reset link has expired.');
        setTokenValid(false);
        return;
      }
      setStudentId(match.id);
      setTokenValid(true);
    };
    checkToken();
  }, [repository, searchParams]);

  const handleSave = async (event) => {
    event.preventDefault();
    setStatus('');
    if (password.length < 8) {
      setStatus('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setStatus('Passwords do not match.');
      return;
    }
    const students = repository.getAll();
    const student = students.find((entry) => entry.id === studentId);
    if (!student) {
      setStatus('Student not found.');
      return;
    }
    const hash = await buildPasswordHash(password);
    repository.update(student.id, {
      ...student,
      portalPasswordHash: hash,
      portalPasswordMustChange: false,
      portalResetTokenHash: '',
      portalResetExpiresAt: null
    });
    setStatus('Password updated. You can now sign in.');
    setPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--sp-surface-alt)' }}>
      <div className="w-full max-w-md space-y-4">
        <StudentPortalBanner settings={portalSettings} />
        <Card className="p-6 w-full space-y-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Reset portal password</h2>
          <p className="text-sm sp-muted">Set a new password for your student portal.</p>
        </div>
        {status && <div className="text-xs sp-warning-text">{status}</div>}
        {tokenValid ? (
          <form className="space-y-3" onSubmit={handleSave}>
            <div>
              <label className="sp-label">New password</label>
              <input
                className="sp-input mt-2 w-full"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            <div>
              <label className="sp-label">Confirm password</label>
              <input
                className="sp-input mt-2 w-full"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </div>
            <Button type="submit" className="w-full">Update password</Button>
          </form>
        ) : (
          <Button variant="secondary" onClick={() => navigate('/student-portal/login')}>
            Back to sign in
          </Button>
        )}
      </Card>
      </div>
    </div>
  );
}
